import type { FC } from 'react';
import { useState } from 'react';
import { ButtonBase, Typography, Chip, Tooltip } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DomainFlowTreeDialog from './DomainFlowTreeDialog';
import type { FlowBiMap } from '../utils/FlowBiMap';

interface DomainFlowTreeTriggerProps {
  flowBiMap: FlowBiMap;
  companyIds: number[];
  branchIds: number[];
  selectedFlows: string[];
  onApply: (domains: string[], flows: string[]) => void;
}

const DomainFlowTreeTrigger: FC<DomainFlowTreeTriggerProps> = ({
  flowBiMap,
  companyIds,
  branchIds,
  selectedFlows,
  onApply,
}) => {
  const [open, setOpen] = useState(false);
  const disabled = companyIds.length === 0;

  return (
    <>
      <Tooltip title={disabled ? 'Select a company first' : 'Select domains and flows'}>
        <span>
          <ButtonBase
            disabled={disabled}
            onClick={() => { setOpen(true); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              height: 36,
              px: 1.5,
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderRadius: '8px',
              cursor: disabled ? 'default' : 'pointer',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover:not(.Mui-disabled)': {
                borderColor: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-disabled': {
                opacity: 0.5,
                pointerEvents: 'none',
              },
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 18, color: 'action.active' }} />
            <Typography variant="body2" noWrap>
              Domain / Flow
            </Typography>
            {selectedFlows.length > 0 && (
              <Chip
                label={selectedFlows.length}
                size="small"
                color="primary"
                sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem' } }}
              />
            )}
            <ArrowDropDownIcon sx={{ fontSize: 20, color: 'action.active', ml: -0.25 }} />
          </ButtonBase>
        </span>
      </Tooltip>
      <DomainFlowTreeDialog
        open={open}
        onClose={() => { setOpen(false); }}
        flowBiMap={flowBiMap}
        companyIds={companyIds}
        branchIds={branchIds}
        selectedFlows={selectedFlows}
        onApply={onApply}
      />
    </>
  );
};

export default DomainFlowTreeTrigger;
